import {AutogenLayoutFuelCard} from "@mcleod/dispatch/src/asset/autogen/AutogenLayoutFuelCard";
import {DataSourceAction, DataSourceExecutionEvent, DataSourceValidationEvent, DropdownItem, LookupModelSelectionEvent} from "@mcleod/components";
import { FuelInterface } from "@mcleod/integrations";
import { ObjectUtil } from "@mcleod/core";

const FUEL_CARD_STATUS_MAP = new Map<string, DropdownItem[]>([
    [FuelInterface.FLEETONE, [
        new DropdownItem("A", "Active"),
        new DropdownItem("I", "Inactive")
    ]],
    [FuelInterface.QUIKO, [
        new DropdownItem("A", "Active"),
        new DropdownItem("I", "Inactive"),
        new DropdownItem("D", "Deleted")
    ]],
    [FuelInterface.COMDATA, [
        new DropdownItem("A", "Active"),
        new DropdownItem("B", "Blocked"),
        new DropdownItem("L", "Lost"),
        new DropdownItem("I", "No company purchase"),
        new DropdownItem("C", "Cloned"),
        new DropdownItem("D", "Deleted"),
        new DropdownItem("X", "Permanently blocked"),
        new DropdownItem("F", "Fraud"),
        new DropdownItem("S", "Stolen")
    ]],
    [FuelInterface.TCHEK, [
        new DropdownItem("A", "Active"),
        new DropdownItem("H", "Hold"),
        new DropdownItem("D", "Deleted"),
        new DropdownItem("I", "Inactive"),
        new DropdownItem("F", "Fraud"),
        new DropdownItem("C", "Cloned")
    ]],
    [FuelInterface.EFS, [
        new DropdownItem("A", "Active"),
        new DropdownItem("H", "Hold"),
        new DropdownItem("D", "Deleted"),
        new DropdownItem("I", "Inactive"),
        new DropdownItem("F", "Fraud"),
        new DropdownItem("C", "Cloned")
    ]]
]);

const COMBINED_CARD_STATUS: DropdownItem[] = [
    new DropdownItem("A", "Active"),
    new DropdownItem("B", "Blocked"),
    new DropdownItem("L", "Lost"),
    new DropdownItem("I", "Inactive"),
    new DropdownItem("C", "Cloned"),
    new DropdownItem("D", "Deleted"),
    new DropdownItem("H", "Hold"),
    new DropdownItem("F", "Fraud")
];

/**
 * FuelCard - CRUD decorator for managing fuel cards
 * Migrated from EntryFuelCard.java
 *
 * The fuel_card table uses a 4-part composite primary key:
 * - id (card number)
 * - account_id
 * - sub_account
 * - fuel_interface_id
 *
 * All four fields must be included in the data source for proper save/update operations.
 */
export class FuelCard extends AutogenLayoutFuelCard {

    override onLoad(): void {
        this.mainDataSource.addValidationListener((event: DataSourceValidationEvent) => {
            this.validateDriverRequirement(event);
        });
    }

    sourceFuelCardAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            this.updateDriverFieldRequirement();
        }
    }

    /**
     * Updates the required state of assignee fields based on Assignee Type
     */
    private updateDriverFieldRequirement(): void {
        const assigneeType = this.textboxAssigneeType.text;

        // Set required state based on assignee type
        const isDriverType = assigneeType === "D" || assigneeType === "Driver";
        const isTractorType = assigneeType === "T" || assigneeType === "Tractor";
        const isPayeeType = assigneeType === "P" || assigneeType === "Payee";

        this.textboxDriverId.required = isDriverType;
        this.textboxTractorId.required = isTractorType;
        this.textboxPayeeId.required = isPayeeType;
    }

    /**
     * Validates that the appropriate assignee field has a value based on Assignee Type
     * Adds validation result to the event's validation results array
     */
    private validateDriverRequirement(event: DataSourceValidationEvent): void {
        // Get the actual data being saved from the event's row
        const row = event.row;
        const assigneeType = row?.getString("assignee_type");
        const driverId = row?.getString("driver_id");
        const tractorId = row?.getString("tractor_id");
        const payeeId = row?.getString("payee_id");

        // Validate Driver is required when Assignee Type is Driver
        if (assigneeType === "D" && (!driverId || driverId.trim() === "")) {
            event.validationResults.push({
                component: this.textboxDriverId,
                isValid: false,
                validationMessage: "Driver is required when Assignee Type is Driver",
                caption: "Driver is required when Assignee Type is Driver"
            });
        }

        // Validate Tractor is required when Assignee Type is Tractor
        if (assigneeType === "T" && (!tractorId || tractorId.trim() === "")) {
            event.validationResults.push({
                component: this.textboxTractorId,
                isValid: false,
                validationMessage: "Tractor is required when Assignee Type is Tractor",
                caption: "Tractor is required when Assignee Type is Tractor"
            });
        }

        // Validate Payee is required when Assignee Type is Payee
        if (assigneeType === "P" && (!payeeId || payeeId.trim() === "")) {
            event.validationResults.push({
                component: this.textboxPayeeId,
                isValid: false,
                validationMessage: "Payee is required when Assignee Type is Payee",
                caption: "Payee is required when Assignee Type is Payee"
            });
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxSubAccount. */
    textboxSubAccountOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const fuelInterfaceId = event.getValueFromSelectedRow("fuel_interface_id");
        const fuelIntefaceLmData = event.selection?.getFirstLookupModelData("fuel_interface_id",  null) ?? null;
        this.activeRow.setValueAndLookupModelData("fuel_interface_id", fuelInterfaceId, fuelIntefaceLmData);
        const accountId = event.getValueFromSelectedRow("master_account");
        this.activeRow.set("account_id", accountId);
        if (fuelInterfaceId) {
            const fuelMapItems = FUEL_CARD_STATUS_MAP.get(fuelInterfaceId);
            if (!ObjectUtil.deepEqual(fuelMapItems, this.textboxStatus.items)) {
                this.activeRow.set("card_status", null);
                this.textboxStatus.items = fuelMapItems ?? COMBINED_CARD_STATUS;
            }
        }
    }
}
